<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { getJobExperienceOptions } from "@/helpers/filterOptions";
import { useConfigurationStore } from "@/store/configurationStore";

import type { supporttedEventNames } from "@/dsComponents/Dropdown/types";
import type { LabelValue } from '@/types/common';

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import { DropdownItem } from "@/dsComponents/Dropdown";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";

type ReferentialMode = "wizim" | "talentplug";

interface ExperiencesDropdownProps {
    modelValue: LabelValue | null;
    placeholder?: string;
    icon?: string;
    width?: string;
    fitContent?: boolean;
    showIcon?: boolean;
    size?: "xs" | "sm";
    referential?: ReferentialMode;
    fullWidth?: boolean;
}

const props = withDefaults(defineProps<ExperiencesDropdownProps>(), {
    icon: "level-3",
    width: "380px",
    showIcon: true,
    fitContent: true,
    size: "sm",
    referential: "wizim",
    fullWidth: false,
});

const emit = defineEmits<(event: "update:modelValue", value: LabelValue | null) => void>();

const { t } = useI18n();
const configStore = useConfigurationStore();

const open = ref(false);

const dropdownExperiences = computed((): LabelValue[] => {
    if (props.referential === "talentplug") {
        return configStore.talentplugApplicantExperience;
    }
    return getJobExperienceOptions();
});

const toggle = () => {
    open.value = !open.value;
};

const handleControlledDropdownChange = (event: supporttedEventNames) => {
    if (event === "clickaway") open.value = false;
};

const handleExperienceSelect = (expValue: string | undefined) => {
    const selected = dropdownExperiences.value.find((item: LabelValue) => item.value === expValue) ?? null;
    emit("update:modelValue", selected);
    open.value = false;
};
</script>
<template>
    <div>
        <ControlledDropdown
            maxHeight="400px"
            :open="open"
            :fit-content="fitContent && !fullWidth"
            :full-width="fullWidth"
            :width="width"
            @change="handleControlledDropdownChange"
        >
            <template #trigger-zone>
                <ComboButton
                    :focus="open"
                    :icon-left="showIcon ? icon : undefined"
                    :placeholder="!modelValue"
                    :size="size"
                    variant="ghost"
                    :fullWidth="fullWidth"
                    @click="toggle"
                >
                    {{ modelValue?.label ?? placeholder ?? t("page.order.experience") }}
                </ComboButton>
            </template>
            <template #dropdown-content>
                <div class="exp-container">
                    <DropdownItem
                        v-for="exp of dropdownExperiences"
                        :key="exp.value"
                        @click="() => handleExperienceSelect(exp.value)"
                        :checked="modelValue?.value === exp.value"
                    >
                        {{ exp.label }}
                    </DropdownItem>
                </div>
            </template>
        </ControlledDropdown>
    </div>
</template>
<style scoped lang="scss">
.exp-container {
    padding-top: 4px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-y: auto;
}
</style>
